import Button from '_atoms/Button/Button';
import i18n from '_i18n';
import {
    SUMMER_2026_DISCOUNT_CODE,
    summerOfferPrices,
} from 'Components/Dashboard/OnboardingReboardingMessages/onboarding/SummerOfferJune2026';
import { getCurrencyFromCountryCode } from 'Components/Shopify/Utils';
import { termsAndConditionsUrl } from 'Helper/Terms';
import { useLanguageAndCurrencyConfig } from 'Hooks/Shopify/useLanguageAndCurrencyConfig';
import useStripeCheckout, {
    CheckoutStripeProps,
} from 'Hooks/useStripeCheckout';
import { CampaignCurrency } from 'Pages/Guest/Onboarding/Offer/OnboardingOfferPage';
import useUser from '_services/Redux/User/Selector/useUser';
import React, { useMemo, useState } from 'react';
import './SummerOfferJune2026Screen.scss';

interface SummerOfferJune2026ScreenProps {
    onAnswer: (answerIndexes: number[]) => void;
}

const summerOriginalYearlyPrices: Record<CampaignCurrency, string> = {
    NOK: '1068 kr',
    USD: '$107',
    EUR: '€107',
    GBP: '£89',
};

const TEXTS = {
    no: (price: string) => ({
        heading: 'Sommertilbud kun i Juni 🌸',
        offerPill: `Få første året med Premium til kun ${price}`,
        priceSuffix: 'betales en gang',
        renewalNote: 'Fornyes til ordinær pris etter 12 måneder.',
        terms: 'Betingelser og vilkår',
        cta: 'Benytt tilbud',
        continueFree: 'Fortsett med begrenset gratisversjon',
    }),
    en: (price: string) => ({
        heading: 'Summer Offer, June Only 🌸',
        offerPill: `Get your first year of Premium for only ${price}`,
        priceSuffix: 'paid once',
        renewalNote: 'Renews at regular price after 12 months.',
        terms: 'Terms & Conditions',
        cta: 'Claim offer',
        continueFree: 'Continue with the limited free version',
    }),
};

const SummerOfferJune2026Screen: React.FC<SummerOfferJune2026ScreenProps> = ({
    onAnswer,
}) => {
    const language: 'no' | 'en' = i18n.language === 'no' ? 'no' : 'en';
    const { user } = useUser();
    const { countryCode } = useLanguageAndCurrencyConfig(user);
    const currency = getCurrencyFromCountryCode(
        countryCode,
    ) as CampaignCurrency;
    const discountedPrice =
        summerOfferPrices[currency] ?? summerOfferPrices.USD;
    const originalPrice =
        summerOriginalYearlyPrices[currency] ?? summerOriginalYearlyPrices.USD;

    const texts = TEXTS[language](discountedPrice);

    const [isLoading, setIsLoading] = useState(false);

    const { onCheckoutButtonClick } = useStripeCheckout();

    const checkoutSuccessUrl = useMemo(() => {
        const successUrl = new URL('/onboarding/offer', window.location.origin);
        successUrl.searchParams.set('checkoutSuccess', 'true');
        return successUrl.toString();
    }, []);

    const handleClaim = (): void => {
        const checkoutParams: CheckoutStripeProps = {
            interval: 'year',
            trial: false,
            discount: SUMMER_2026_DISCOUNT_CODE,
            success_url: checkoutSuccessUrl,
            isLoading: setIsLoading,
            onDone: () => setIsLoading(false),
        };
        setIsLoading(true);
        onCheckoutButtonClick(checkoutParams);
    };

    return (
        <div className="summer-offer-june-2026-screen">
            <div className="summer-offer-june-2026-content">
                <h1 className="summer-offer-june-2026-heading">
                    {texts.heading}
                </h1>
                <div className="summer-offer-june-2026-pill">
                    {texts.offerPill}
                </div>
                <div className="summer-offer-june-2026-price">
                    <span className="summer-offer-june-2026-price-original">
                        {originalPrice}
                    </span>
                    <span className="summer-offer-june-2026-price-discounted">
                        {discountedPrice} {texts.priceSuffix}
                    </span>
                </div>
                <div className="summer-offer-june-2026-renewal">
                    {texts.renewalNote}
                </div>
            </div>

            <div className="summer-offer-june-2026-bottom">
                <button
                    type="button"
                    className="summer-offer-june-2026-terms"
                    onClick={() =>
                        window.open(
                            termsAndConditionsUrl,
                            '_blank',
                            'noopener,noreferrer',
                        )
                    }
                >
                    {texts.terms}
                </button>
                <Button
                    classes="summer-offer-june-2026-claim"
                    isDisabled={isLoading}
                    isLoading={isLoading}
                    onClick={handleClaim}
                >
                    {texts.cta}
                </Button>
                <button
                    type="button"
                    className="summer-offer-june-2026-continue-free"
                    onClick={() => onAnswer([4])}
                >
                    {texts.continueFree}
                </button>
            </div>
        </div>
    );
};

export default SummerOfferJune2026Screen;
